import React, { useEffect, useState } from 'react';
import { Loader2, ShieldCheck, ShieldOff, Info } from 'lucide-react';
import { getGatewayGuardrails, setGatewayGuardrails, type GuardrailsState } from '../../services/adminService';

/**
 * Admin control for the AI-gateway's LLM content-safety guardrail (for THIS environment's gateway). When ON, the
 * gateway runs a safety model on EVERY request and response — thorough, but it BURNS TOKENS on every call. When OFF,
 * we rely on the token-free in-house checks (deterministic negativity/abuse screen), the always-on agent charter, and
 * the model's own native safety — no extra model call. Default is OFF to keep spend down; turn ON for a stricter posture.
 */
const GuardrailsPanel: React.FC = () => {
  const [state, setState] = useState<GuardrailsState | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    getGatewayGuardrails()
      .then(setState)
      .catch(() => setError('Could not read the gateway guardrail state.'))
      .finally(() => setLoading(false));
  }, []);

  const toggle = async (next: boolean) => {
    setSaving(true); setError('');
    try {
      const s = await setGatewayGuardrails(next);
      setState(s);
      if (!s.configured || s.message) setError(s.message || 'The gateway is not configured in this environment.');
    } catch { setError('Update failed — please try again.'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="rounded-2xl border border-secondary-200 dark:border-secondary-800 p-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-secondary-400" /></div>;

  const configured = !!state?.configured;
  const enabled = !!state?.enabled;

  return (
    <div className="rounded-2xl border border-secondary-200 dark:border-secondary-800 p-5">
      <div className="mb-1 flex items-center justify-between">
        <h2 className="font-semibold">Model safety guardrail <span className="text-secondary-400 font-normal text-sm">· gateway{state?.gateway ? ` · ${state.gateway}` : ''}</span></h2>
        {enabled
          ? <ShieldCheck className="w-5 h-5 text-secondary-700 dark:text-secondary-200" />
          : <ShieldOff className="w-5 h-5 text-secondary-400" />}
      </div>
      <p className="text-xs text-secondary-500 mb-4">
        When on, a safety model screens every request and reply — thorough, but it uses tokens on every call. When off,
        the built-in token-free checks, the agent charter, and the model&apos;s own safety still apply at no extra cost.
      </p>

      {/* On / Off toggle */}
      <div className={`grid grid-cols-2 gap-2 ${configured ? '' : 'opacity-40'}`}>
        {([[false, 'Off', 'No extra model call', ShieldOff], [true, 'On', 'Screens every call', ShieldCheck]] as const).map(([val, label, sub, Icon]) => (
          <button key={label} onClick={() => toggle(val)} disabled={saving || !configured || enabled === val}
            className={`flex items-start gap-2 rounded-xl border px-3 py-2.5 text-left transition disabled:cursor-not-allowed ${enabled === val
              ? 'border-secondary-900 dark:border-white bg-secondary-50 dark:bg-secondary-800'
              : 'border-secondary-200 dark:border-secondary-700 enabled:hover:border-secondary-300'}`}>
            <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${enabled === val ? 'text-secondary-900 dark:text-white' : 'text-secondary-400'}`} />
            <span className="min-w-0">
              <span className={`block text-sm ${enabled === val ? 'font-semibold text-secondary-900 dark:text-white' : 'text-secondary-600 dark:text-secondary-300'}`}>{label}{saving && enabled !== val && <Loader2 className="inline w-3 h-3 ml-1.5 animate-spin" />}</span>
              <span className="block text-xs text-secondary-400">{sub}</span>
            </span>
          </button>
        ))}
      </div>

      {!configured && (
        <p className="mt-3 flex items-start gap-1.5 text-xs text-secondary-400">
          <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" /> The AI gateway isn&apos;t configured in this environment, so there&apos;s nothing to toggle here.
        </p>
      )}
      {error && <p className="mt-3 text-xs text-amber-600 dark:text-amber-500">{error}</p>}
    </div>
  );
};

export default GuardrailsPanel;
